import React, { useEffect, useState } from "react";
import { Search, Filter, Plus, Pencil, Power } from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import Pagination from "../../../components/ui/Pagination";
import { Badge } from "../../../components/ui/Badge";
import AddTenantUserModal from "src/components/modals/AddTenantUserModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantUserActions from "src/redux/slices/tenantUserSlice";
import { formatDateTime } from "src/utils/date-time";
import { toast } from "react-toastify";
import PageHeader from "src/components/common/PageHeader";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import ConfirmationModal from "src/components/modals/ConfirmationModal";

const TenantUsersPage: React.FC = () => {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);

  useEffect(() => {
    if (user) {
      setTenantId(user.tenantId || "");
    }
  }, [user]);

  return (
    <DashboardLayout
      meta={{
        title: "Tenant Users | Invyntra",
        description:
          "Track and manage all tenant users with detailed information.",
        keywords: "tenant users, details, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title="Users"
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Users", path: "/admin/tenant/list" },
          ]}
        />
      </div>

      <div className="bg-white border rounded-xl p-4 shadow-sm mt-4">
        <CardHeader
          title="Users"
          description="List of Users"
          actions={<div></div>}
        />

        {tenantId && (
          <div className="pt-4 -mt-16">
            <TenantUsersList tenantId={tenantId} type="manager" />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TenantUsersPage;

interface TenantUsersListProps {
  tenantId: string;
  type?: "admin" | "manager";
}

export const TenantUsersList: React.FC<TenantUsersListProps> = ({
  tenantId = "",
  type = "admin",
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<any | null>(null);
  const [limit, setLimit] = useState(10);

  const { tenantUserList, editTenantUser } = useAppSelector(
    (state) => state.tenantUser
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    getTenantUsersList(1);
  }, []);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editTenantUser.data && statusUpdate) {
      setStatusUpdate(false);
      toast.success(
        `${type === "admin" ? "Tenant User" : "User"} modified successfully`
      );
      dispatch(tenantUserActions.resetEditTenantUser());
      handleStatusModalClose();
      getTenantUsersList(1);
    } else if (editTenantUser.error && statusUpdate) {
      toast.error(`Error: ${editTenantUser.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
  }, [editTenantUser]);

  const getTenantUsersList = (page: number) =>
    dispatch(
      tenantUserActions.getTenantUserListRequest({
        page,
        limit,
        tenantId,
        isAdminAuth: true,
      })
    );

  const handleAddUserModalOpen = () => {
    setOpenModal(true);
    setSelectedUser(null);
  };

  const handleAddPlanModalClose = () => {
    setStatusUpdate(false);
    setOpenModal(false);
    getTenantUsersList(1);
    setSelectedUser(null);
  };

  const handleEdit = (data: any) => {
    const newData = {
      ...data,
      role: data?.role?._id || data?.role,
      password: "",
    };
    setSelectedUser(newData);
    setOpenModal(true);
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    dispatch(
      tenantUserActions.editTenantUserRequest({
        _id: statusRow._id,
        status: statusRow.status === "active" ? "suspended" : "active",
        tenantId,
        isAdminAuth: true,
      })
    );
  };

  const handlePageChange = (page: number) => {
    getTenantUsersList(page);
  };

  return (
    <div>
      {/* Top Actions */}
      <CardHeader
        title=""
        description=""
        actions={
          <Button variant="primary" onClick={handleAddUserModalOpen}>
            <Plus size={16} className="mr-1" />{" "}
            {type === "manager" ? `Add User` : `Add Tenant User`}
          </Button>
        }
      />

      {/* Table */}
      <TableV2
        columns={[
          {
            key: "name",
            header: "Name",
            accessor: ({ key, row }) => (
              <div className="w-20">
                {row["firstName"] + " " + row["lastName"]}
              </div>
            ),
          },
          { key: "email", header: "Email" },
          { key: "gender", header: "Gender" },
          {
            key: "role",
            header: "Role",
            accessor: ({ row }) => <div>{row?.role?.name || row?.role}</div>,
          },
          {
            key: "createdAt",
            header: "Created At",
            accessor: ({ key, row }) => (
              <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
            ),
          },
          {
            key: "status",
            header: "Status",
            accessor: ({ key, row }) => (
              <Badge
                text={row?.[key] || ""}
                color={row?.[key] === "active" ? "green" : "red"}
              />
            ),
          },
          {
            key: "actions",
            header: "Actions",
            accessor: ({ row }) => (
              <div className="pr-2">
                <button
                  className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                  onClick={() => handleEdit(row)}
                >
                  <Pencil size={14} />
                </button>
                <button
                  className={`p-2 rounded-md ${
                    row.status === "active"
                      ? "hover:bg-red-100 text-red-600"
                      : "hover:bg-green-100 text-green-600"
                  }`}
                  onClick={() => {
                    setStatusRow(row);
                    setShowStatusModal(true);
                  }}
                >
                  <Power size={14} />
                </button>
              </div>
            ),
          },
        ]}
        data={tenantUserList?.data?.data}
        pagination={tenantUserList?.data?.pagination}
        onPageChange={handlePageChange}
      />

      {/* Add Plan Modal */}
      {openModal && (
        <AddTenantUserModal
          open={openModal}
          userData={selectedUser}
          onClose={handleAddPlanModalClose}
          tenantId={tenantId}
          type={type}
        />
      )}

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow.status === "active" ? "suspend" : "activate"
          } this user?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editTenantUser.loading}
        />
      )}
    </div>
  );
};
